import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { CommandeService } from 'src/app/core/services/commande.service';
import { StatistiqueService } from 'src/app/core/services/statistique.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { SharedUtilsService } from 'src/app/core/services/shared-utils.service';
import { MessageService } from 'src/app/core/services/message.service';
import { SseNotificationService } from 'src/app/core/services/sse-notification.service';

@Component({
  selector: 'app-commandes-interurbaines',
  templateUrl: './commandes-interurbaines.component.html',
  styleUrls: ['./commandes-interurbaines.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CommandesInterurbainesComponent implements OnInit {

  commandes: any[] = [];
  commandesFiltrees: any[] = [];

  indicateurChargement = true;
  pageCourante = 1;
  taillePage = 20;
  totalElements = 0;

  // Filtres
  recherche = '';
  statutFiltre = '';
  typeFiltre = '';

  // KPIs calculés côté client
  kpis = { total: 0, enTransit: 0, livrees: 0, incidents: 0, aEncaisser: 0 };

  // Panel assignation livreur
  panelAffectationOuvert = false;
  commandeCibleAffectation: any = null;
  assignationEnCoursIds = new Set<number>();
  montantContrat: number | null = null;
  rechercheLivreur = '';
  chargementLivreurs = false;
  livreursPanel: any[] = [];
  livreursPanelFiltres: any[] = [];

  readonly STATUTS = [
    { valeur: '', libelle: 'Tous les statuts' },
    { valeur: 'EN_ATTENTE',      libelle: 'En attente' },
    { valeur: 'AFFECTE',         libelle: 'Livreur assigné' },
    { valeur: 'ENLEVEMENT_EN_COURS', libelle: 'Enlèvement en cours' },
    { valeur: 'RECUPERE',        libelle: 'Colis récupéré' },
    { valeur: 'AU_DEPOT',        libelle: 'Au dépôt' },
    { valeur: 'EN_TRI',          libelle: 'En tri' },
    { valeur: 'EN_TRANSIT',      libelle: 'En transit' },
    { valeur: 'RECEPTIONNE',     libelle: 'Réceptionné' },
    { valeur: 'EN_DISTRIBUTION', libelle: 'En distribution' },
    { valeur: 'EN_LIVRAISON',    libelle: 'En livraison' },
    { valeur: 'LIVRE',           libelle: 'Livré' },
    { valeur: 'INCIDENT',        libelle: 'Incident' },
    { valeur: 'ANNULE',          libelle: 'Annulé' },
  ];

  readonly TYPES = [
    { valeur: '', libelle: 'Tous les types' },
    { valeur: 'EXPRESS_INTERURBAIN',  libelle: 'Express interurbain' },
    { valeur: 'STANDARD_INTERURBAIN', libelle: 'Standard interurbain' },
  ];

  constructor(
    private commandeService: CommandeService,
    private statistiqueService: StatistiqueService,
    public router: Router,
    private toast: ToastService,
    private sharedUtils: SharedUtilsService,
    private messageService: MessageService,
    private sse: SseNotificationService
  ) {}

  ngOnInit(): void {
    this.charger();
  }

  charger(): void {
    this.indicateurChargement = true;
    this.commandeService.obtenirCommandesInterurbaines(this.pageCourante - 1, this.taillePage).subscribe({
      next: (data: any) => {
        this.commandes = data?.content ?? [];
        this.totalElements = data?.totalElements ?? 0;
        this.appliquerFiltres();
        this.calculerKpis();
        this.indicateurChargement = false;
      },
      error: () => { this.indicateurChargement = false; }
    });
  }

  appliquerFiltres(): void {
    let resultat = [...this.commandes];
    if (this.recherche.trim()) {
      const q = this.recherche.toLowerCase();
      resultat = resultat.filter(c =>
        (c.numeroCommande?.toLowerCase().includes(q)) ||
        (c.expediteur?.nom?.toLowerCase().includes(q)) ||
        (c.destinataire?.nom?.toLowerCase().includes(q)) ||
        (c.livreur?.prenom?.toLowerCase().includes(q))
      );
    }
    if (this.statutFiltre) {
      resultat = resultat.filter(c => c.statut === this.statutFiltre);
    }
    if (this.typeFiltre) {
      resultat = resultat.filter(c => c.typeEnvoi === this.typeFiltre);
    }
    this.commandesFiltrees = resultat;
  }

  calculerKpis(): void {
    this.kpis.total      = this.commandes.length;
    this.kpis.enTransit  = this.commandes.filter(c => ['EN_TRANSIT', 'RECEPTIONNE', 'EN_DISTRIBUTION'].includes(c.statut)).length;
    this.kpis.livrees    = this.commandes.filter(c => c.statut === 'LIVRE').length;
    this.kpis.incidents  = this.commandes.filter(c => c.statut === 'INCIDENT').length;
    this.kpis.aEncaisser = this.commandes.filter(c => c.statut === 'LIVRE' && !c.cashEncaisseParLivreur).length;
  }

  voirDetail(id: number): void {
    this.router.navigate(['/commande', id]);
  }

  badgeStatut(statut: string): string {
    const map: Record<string, string> = {
      EN_ATTENTE: 'badge--gris',
      AFFECTE: 'badge--bleu-clair',
      ENLEVEMENT_EN_COURS: 'badge--orange',
      RECUPERE: 'badge--jaune',
      AU_DEPOT: 'badge--violet',
      EN_TRI: 'badge--violet',
      EN_TRANSIT: 'badge--bleu',
      RECEPTIONNE: 'badge--teal',
      EN_DISTRIBUTION: 'badge--teal',
      EN_LIVRAISON: 'badge--vert-clair',
      LIVRE: 'badge--vert',
      INCIDENT: 'badge--rouge',
      ANNULE: 'badge--gris-fonce',
      EN_RETARD: 'badge--rouge-clair',
    };
    return map[statut] ?? 'badge--gris';
  }

  libelleStatut(statut: string): string {
    return this.STATUTS.find(s => s.valeur === statut)?.libelle ?? statut;
  }

  libelleType(type: string): string {
    return this.TYPES.find(t => t.valeur === type)?.libelle ?? type;
  }

  changerPage(page: number): void {
    this.pageCourante = page;
    this.charger();
  }

  readonly Math = Math;

  get totalPages(): number {
    return Math.ceil(this.totalElements / this.taillePage);
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  reinitialiserFiltres(): void {
    this.recherche = '';
    this.statutFiltre = '';
    this.typeFiltre = '';
    this.appliquerFiltres();
  }

  // ─── Panel assignation livreur ─────────────────────────────────────────────

  ouvrirPanelAssignation(commande: any): void {
    if (!commande?.id || this.assignationEnCoursIds.has(commande.id)) return;
    this.commandeCibleAffectation = commande;
    this.panelAffectationOuvert = true;
    this.rechercheLivreur = '';
    this.montantContrat = null;
    this.chargementLivreurs = true;
    this.livreursPanel = [];
    this.livreursPanelFiltres = [];

    this.sharedUtils.obtenirLivreursDisponibles(commande.id).subscribe({
      next: (reponse: any) => {
        const liste = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
        this.livreursPanel = liste;
        this.livreursPanelFiltres = [...liste];
        this.chargementLivreurs = false;
      },
      error: (erreur: any) => {
        this.chargementLivreurs = false;
        this.livreursPanel = [];
        this.livreursPanelFiltres = [];
        this.toast.erreur('Impossible de charger les livreurs : ' + this.messageService.afficherMessageErreur(erreur));
      }
    });
  }

  fermerPanelAssignation(): void {
    const id = this.commandeCibleAffectation?.id;
    if (id != null && this.assignationEnCoursIds.has(id)) return;
    this.panelAffectationOuvert = false;
    this.commandeCibleAffectation = null;
  }

  filtrerLivreursPanel(): void {
    const terme = (this.rechercheLivreur || '').trim().toLowerCase();
    if (!terme) { this.livreursPanelFiltres = [...this.livreursPanel]; return; }
    this.livreursPanelFiltres = this.livreursPanel.filter((l: any) =>
      `${l?.nom || ''} ${l?.prenom || ''}`.toLowerCase().includes(terme) ||
      `${l?.contact || ''}`.toLowerCase().includes(terme)
    );
  }

  assignerLivreurDepuisPanel(livreur: any): void {
    const idCommande = this.commandeCibleAffectation?.id;
    if (!idCommande || !livreur?.id || this.assignationEnCoursIds.has(idCommande)) return;

    this.assignationEnCoursIds.add(idCommande);
    this.sse.ignorerProchainToastAffectationLivreur(idCommande);

    this.sharedUtils.attribuerLivreurManuellement(idCommande, livreur.id, undefined).subscribe({
      next: (donnees: any) => {
        this.assignationEnCoursIds.delete(idCommande);
        this.toast.succes(`Livreur ${donnees.livreurNom ?? livreur.nom} assigné avec succès`, 'Assignation');
        this.panelAffectationOuvert = false;
        this.commandeCibleAffectation = null;
        this.charger();
      },
      error: (erreur: any) => {
        this.assignationEnCoursIds.delete(idCommande);
        this.toast.erreur('Erreur lors de l\'affectation : ' + this.messageService.afficherMessageErreur(erreur));
      }
    });
  }

  libelleTypeVehicule(livreur: any): string {
    const type = livreur?.vehiculeActuel?.typeVehicule;
    if (!type) return '';
    const labels: Record<string, string> = {
      MOTO: 'Moto', VOITURE: 'Voiture', CAMIONNETTE: 'Camionnette', VELO: 'Vélo', TRICYCLE: 'Tricycle'
    };
    return labels[type] ?? type;
  }
}
